import { forwardRef, useEffect, useMemo, useState } from "react";

import { useIntersectionObserver } from "@uidotdev/usehooks";
import { useDebounce } from "@uidotdev/usehooks";
import { Command } from "cmdk";

import { closeCommandPalette } from "../../commands";
import {
  LIMIT,
  searchIsEnabled,
} from "../../constants/command-palette.constants";
import { DEFAULT_FILTER } from "../../constants/filter.constants";
import { useInfiniteSearchContentQuery } from "../../lib/__generated__/graphql";
import { graphqlClient } from "../../lib/graphql/graphql-request";
import { CommandPaletteBody } from "./command-palette-body";
import { CommandPaletteFooter } from "./command-palette-footer";
import { CommandPaletteSearch } from "./command-palette-search";

export const CommandPalette = forwardRef<HTMLDialogElement>((_props, ref) => {
  /* State */
  const [searchValue, setSearchValue] = useState("");

  /* Debounced State */
  const DEBOUNCE_DELAY = 500;
  const debouncedSearchValue = useDebounce(searchValue, DEBOUNCE_DELAY);

  /* Handlers */
  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  /* Query & Query Helpers */
  const enabled = searchIsEnabled(debouncedSearchValue);

  const { data, isLoading, fetchNextPage, isFetching } =
    useInfiniteSearchContentQuery(
      graphqlClient,
      {
        input: {
          query: debouncedSearchValue,
          contentTypes: DEFAULT_FILTER.value,
        },
        page: {
          limit: LIMIT,
          offset: 0,
        },
      },
      {
        enabled,
        getNextPageParam: (lastPage, allPages) => {
          // determine whether not there is a next page
          if (lastPage.search.totalItems <= allPages.length * LIMIT) {
            return undefined;
          }

          return {
            page: {
              limit: LIMIT,
              offset: allPages.length * LIMIT,
            }, // this will increment the offset by LIMIT
            hasNextPage: true,
          };
        },
        initialPageParam: 0,
      },
    );

  /* Flattened Results to negate the need for nested mapping */
  const flattenedResults = useMemo(() => {
    if (!data?.pages) {
      return [];
    }
    return data.pages
      .flatMap((page) => page.search.items)
      .map((item) => ({
        id: item.id,
        title: item.title,
        link: item.link,
        description: item?.description || "",
        type: item.type,
      }));
  }, [data?.pages]);

  /* Infinite Scrolling Logic */
  const [scrollRef, entry] = useIntersectionObserver({
    threshold: 0.9,
    root: null,
    rootMargin: "0px",
  });

  useEffect(() => {
    if (entry?.isIntersecting && enabled) {
      fetchNextPage();
    }
  }, [entry?.isIntersecting, fetchNextPage, enabled]);

  return (
    <dialog
      ref={ref}
      aria-labelledby="plh-dialog-title"
      aria-describedby="plh-dialog-description"
      onClose={() => {
        setSearchValue("");
        closeCommandPalette();
      }}
    >
      <h2 id="plh-dialog-title" className="plh-sr-only">
        Search PennyLane.ai
      </h2>
      <p id="plh-dialog-description" className="plh-sr-only">
        Begin by typing to search for content on PennyLane.ai
      </p>
      <Command
        shouldFilter={false}
        loop={false}
        label="Search PennyLane.ai"
        className="border-gray-400 plh-fixed plh-bottom-0 plh-left-1/2 plh-z-[9999] plh-w-full plh-translate-x-[-50%] plh-overflow-hidden plh-rounded-t-[5px] plh-border-x plh-border-none plh-bg-white plh-shadow md:plh-bottom-auto md:plh-top-[15%] md:plh-w-[574px] md:plh-rounded-b-[5px] sm-h:plh-inset-0 sm-h:plh-w-full sm-h:plh-translate-x-0 sm-h:plh-rounded-none sm-h:plh-shadow-none"
      >
        <div className="plh-overflow-hidden">
          {/* Search Input */}
          <CommandPaletteSearch
            searchValue={searchValue}
            onSearchValueChange={handleSearch}
            onClear={() => setSearchValue("")}
          />

          {/* Results/Body */}
          <CommandPaletteBody
            options={flattenedResults}
            ref={scrollRef}
            query={debouncedSearchValue}
            isLoading={isLoading}
            isFetching={isFetching}
            totalOptions={data?.pages[0]?.search.totalItems || 0}
            clearQuery={() => setSearchValue("")}
          />

          {/* Footer */}
          <CommandPaletteFooter
            count={flattenedResults.length}
            total={data?.pages[0]?.search.totalItems || 0}
            query={debouncedSearchValue}
            isLoading={isLoading}
          />
        </div>
      </Command>
    </dialog>
  );
});
